/**
 * Scheduled payment rows for the enrollment agreement templates, flattened to
 * each row's own fields.
 *
 * The rows reach the templates in two shapes:
 *  - the enrollment form's FormArray value: a flat array of
 *    { field_payment_amount, field_payment_date, field_down_payment, ... }
 *  - the saved entity via the ECK REST wrapper, which nests every referenced
 *    scheduled payment as { bundle, type, 0: { ...fields } } and collapses a
 *    one-row field to that object instead of a one-element array.
 *
 * Reading field_payment_amount straight off the nested shape yields undefined,
 * so an agreement rendered from the saved entity printed no payment plan and a
 * $0.00 down payment. Returns [] when there are no rows.
 */
export function agreementScheduledPayments(raw: any): any[] {
  if (!raw || typeof raw !== 'object') return [];

  let list: any[];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (isCollapsedReference(raw)) {
    list = [raw];
  } else {
    // An object keyed by numeric index ({ '0': {...}, '1': {...} }).
    list = Object.keys(raw).filter(k => !isNaN(Number(k))).map(k => raw[k]);
  }

  return list
    .map(row => (isCollapsedReference(row) ? row['0'] : row))
    .filter(row => row && typeof row === 'object');
}

/** { bundle, type, 0: { ...fields } } as the ECK REST wrapper nests a referenced entity. */
function isCollapsedReference(row: any): boolean {
  return !!row && typeof row === 'object' && !Array.isArray(row)
    && row['0'] && typeof row['0'] === 'object'
    && ('bundle' in row || 'type' in row);
}
